import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardMedia,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Menu,
  Autocomplete,
  Snackbar,
  FormControlLabel,
  Switch,
  Tooltip,
  Alert,
  Collapse,
  Popover,
  OutlinedInput,
  TablePagination,
  CircularProgress,
} from '@mui/material';
import { toast } from 'react-toastify';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Print as PrintIcon,
  Visibility as VisibilityIcon,
  MoreVert as MoreVertIcon,
  Close as CloseIcon,
  Receipt as ReceiptIcon,
  Warning as WarningIcon,
  HourglassEmpty as HourglassEmptyIcon,
  DoneAll as DoneAllIcon,
  VisibilityOff as VisibilityOffIcon,
  CalendarToday as CalendarTodayIcon,
  AccountBalance as AccountBalanceIcon,
  TrendingUp as TrendingUpIcon,
  Brush as BrushIcon,
  Refresh as RefreshIcon,
  Email as EmailIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  AttachFile as AttachFileIcon,
  PictureAsPdf as PdfIcon,
  Payments as PaymentsIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
// import { collection, addDoc, query, where, getDocs, updateDoc, doc, getDoc, orderBy, writeBatch, serverTimestamp, limit } from 'firebase-shim';
// import { db, auth } from '../firebase/config';
import indexedDBService from '../services/IndexedDBService';
import { generateInvoicePDF } from '../utils/invoicePdfGenerator';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { InventoryItem } from '../types/inventory';
import { InvoiceTemplateType, getTemplatePreferenceSync, getTemplatePreference, invoiceTemplateOptions } from '../utils/invoiceTemplates';
// import { logEvent } from 'firebase-shim';
// import { analytics } from '../firebase/config';
import { BusinessData } from '../types/business';
import BusinessProfileUpdateDialog from '../components/dialogs/BusinessProfileUpdateDialog';
import CreateInvoiceDialog from '../components/dialogs/CreateInvoiceDialog';
import InvoiceService from '../services/invoiceService';
import EmailService from '../utils/emailService';
import SettingsService from '../services/settingsService';

interface Invoice {
  id: string;
  invoiceNumber: string;
  client: string;
  clientId?: string;
  clientDetails: {
    address: string;
    gstin: string;
    email: string;
    phone: string;
    state: string;
  };
  date: string;
  dueDate: string;
  paymentDate?: string;
  items: Array<{
    id: number;
    name: string;
    description: string;
    quantity: number;
    rate: number;
    amount: number;
    inventoryItemId?: string;
    hsnCode?: string;
    gstRate?: number;
    cgst?: number;
    sgst?: number;
    igst?: number;
    totalAmount?: number;
    taxAmount?: number;
  }>;
  notes: string;
  termsAndConditions: string;
  taxType: string;
  taxRate: number;
  taxable: boolean;
  discountType: string;
  discountValue: number;
  subtotal: number;
  tax: number;
  discount: number;
  amount: number;
  status: 'draft' | 'pending' | 'paid' | 'overdue' | 'deleted';
  createdAt: string;
  userId: string;
  template?: number; // Add this field for template ID
  paymentInfo?: {
    amountReceived: number;
    paymentMode: 'cash' | 'bank';
    bankAccount?: {
      bank: string;
      accountNumber: string;
    };
    paymentDate: string;
    paymentReference?: string;
  };
  // Add Indian GST specific fields
  placeOfSupply?: string;
  eWayBill?: string;
  transportMode?: string;
  vehicleNumber?: string;
  reverseCharge?: 'Y' | 'N';
  deliveryAddress?: string;
}

// Add a dedicated interface for invoice items
interface InvoiceItem {
  id: number;
  name: string;
  description: string;
  quantity: number;
  rate: number;
  amount: number;
  inventoryItemId?: string;
  hsnCode?: string;
  gstRate?: number;
  taxAmount?: number; // Add taxAmount property
}

// Define invoice data state type
interface InvoiceDataState {
  invoiceNumber: string;
  client: string;
  clientId: string;
  clientAddress: string;
  clientGSTIN: string;
  clientEmail: string;
  clientPhone: string;
  invoiceDate: string;
  dueDate: string;
  paymentDate: string;
  notes: string;
  termsAndConditions: string;
  taxType: string;
  taxRate: number;
  taxable: boolean;
  discountType: string;
  discountValue: number;
  status: 'draft' | 'pending' | 'paid' | 'overdue' | 'deleted';
  id?: string;
  createdAt?: string;
  template?: number | null;
  placeOfSupply?: string;
  eWayBill?: string;
  transportMode?: string;
  vehicleNumber?: string;
  reverseCharge?: 'Y' | 'N';
  deliveryAddress?: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: any;
  value: any;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`invoice-tabpanel-${index}`}
      aria-labelledby={`invoice-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 0, pt: 2 }}>{children}</Box>}
    </div>
  );
}

function Invoices() {
  const { currentUser, userProfile } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterMenuAnchor, setFilterMenuAnchor] = useState<null | HTMLElement>(null);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [dateFilter, setDateFilter] = useState<string | null>(null);
  const [viewInvoiceDialogOpen, setViewInvoiceDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteInvoiceId, setDeleteInvoiceId] = useState<string | null>(null);
  const [businessDetails, setBusinessDetails] = useState<BusinessData | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchAnchorEl, setSearchAnchorEl] = useState<null | HTMLElement>(null);
  const [showCards, setShowCards] = useState(true);
  const [clients, setClients] = useState<any[]>([]);
  const [inventory, setInventory] = useState<any[]>([]);

  // Summary stats state
  const [summaryStats, setSummaryStats] = useState({
    totalInvoices: 0,
    monthlyInvoices: 0,
    pendingAmount: 0,
    overdueAmount: 0
  });

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning'
  });

  // Add the following state variables
  const [openBusinessProfileDialog, setOpenBusinessProfileDialog] = useState<boolean>(false);
  const [missingBusinessFields, setMissingBusinessFields] = useState<string[]>([]);
  const [createInvoiceDialogOpen, setCreateInvoiceDialogOpen] = useState(false);

  // Email dialog state
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailTo, setEmailTo] = useState('');
  const [emailPdfBytes, setEmailPdfBytes] = useState<Uint8Array | null>(null);
  const [emailSending, setEmailSending] = useState(false);

  // Record Payment state variables
  const [recordPaymentOpen, setRecordPaymentOpen] = useState(false);
  const [paymentInvoice, setPaymentInvoice] = useState<Invoice | null>(null);
  const [paymentMode, setPaymentMode] = useState<'cash' | 'bank'>('cash');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentReference, setPaymentReference] = useState('');
  const [selectedBankIdx, setSelectedBankIdx] = useState(0);

  // Fetch invoices when component mounts
  useEffect(() => {
    setLoading(true);
    const fetchAllData = async () => {
      await Promise.all([
        fetchInvoices(),
        fetchBusinessDetails(),
        fetchClients(),
        fetchInventoryItems()
      ]);
      setLoading(false);
    };

    fetchAllData();

    // Check if we have a state to open new invoice
    if (location.state && (location.state as any).openNewInvoice) {
      handleCreateNewInvoice();
      // Clear the state after handling it
      window.history.replaceState({}, document.title);
    }
  }, [currentUser, location]);

  const fetchBusinessDetails = async () => {
    if (!currentUser) return;

    try {
      // First check if we already have business details in the user profile
      if (userProfile && userProfile.businessName) {
        setBusinessDetails({
          id: userProfile.uid,
          name: userProfile.businessName,
          businessName: userProfile.businessName,
          gstin: userProfile.gstin || '',
          address: userProfile.address || '',
          city: userProfile.city || '',
          state: userProfile.state || '',
          pincode: userProfile.pincode || '',
          email: userProfile.email || '',
          phone: userProfile.phone || '',
        });
        return;
      }

      const businesses = await indexedDBService.getAllItems<any>('businesses');
      if (businesses.length > 0) {
        const businessData = businesses[0]; // Simplification for local DB
        setBusinessDetails({
          id: businessData.id,
          name: businessData.name || businessData.businessName || '',
          businessName: businessData.businessName || businessData.name || '',
          gstin: businessData.gstin || businessData.GSTIN || '',
          address: businessData.address || '',
          email: businessData.email || '',
          phone: businessData.phone || businessData.phoneNumber || '',
          website: businessData.website || '',
          bankDetails: businessData.bankDetails || {},
          state: businessData.state || '',
          city: businessData.city || '',
          pincode: businessData.pincode || '',
        });
      } else {
        console.error('No business details found');
        setMissingBusinessFields(['name', 'address']);
        setOpenBusinessProfileDialog(true);
      }
    } catch (error) {
      console.error('Error fetching business details:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load business details',
        severity: 'error'
      });
    }
  };

  // Add helper functions for localStorage client caching
  const saveClientsToLocalStorage = (clientData: any[]) => {
    try {
      if (clientData && clientData.length > 0) {
        localStorage.setItem('cachedClients', JSON.stringify(clientData));
      }
    } catch (error) {
      console.error('Error saving clients to localStorage:', error);
    }
  };

  const getClientsFromLocalStorage = () => {
    try {
      const cachedClients = localStorage.getItem('cachedClients');
      if (cachedClients) {
        return JSON.parse(cachedClients);
      }
    } catch (error) {
      console.error('Error reading clients from localStorage:', error);
    }
    return null;
  };

  // Extract clients from existing invoices as a last resort
  const extractClientsFromInvoices = () => {
    if (!invoices || invoices.length === 0) return [];

    const clientsFromInvoices = new Map();

    invoices.forEach(invoice => {
      // Skip if we don't have client info
      if (!invoice.client) return;

      // Create a unique ID based on client name or whatever data we have
      const clientId = invoice.clientId || `client-${invoice.client.replace(/\s+/g, '-').toLowerCase()}`;

      if (!clientsFromInvoices.has(clientId)) {
        clientsFromInvoices.set(clientId, {
          id: clientId,
          name: invoice.client,
          address: invoice.clientDetails?.address || '',
          gstin: invoice.clientDetails?.gstin || '',
          email: invoice.clientDetails?.email || '',
          phone: invoice.clientDetails?.phone || '',
          state: invoice.clientDetails?.state || '',
          // Add a flag to indicate this came from invoice data
          extractedFromInvoice: true
        });
      }
    });

    return Array.from(clientsFromInvoices.values());
  };

  const fetchClients = async () => {
    if (!currentUser) return;

    try {
      const clientsData = await indexedDBService.getAllItems<any>('clients');

      // Normalize and filter valid clients
      const validClientsData = clientsData
        .map(client => ({
          id: client.id,
          ...client,
          name: client.name || client.clientName || 'Unnamed Client',
          address: client.address || client.clientAddress || '',
          gstin: client.gstin || client.GSTIN || '',
          email: client.email || client.clientEmail || '',
          phone: client.phone || client.phoneNumber || '',
          state: client.state || client.clientState || ''
        }))
        .filter(client => client && client.id && (client.name || client.email || client.phone));

      setClients(validClientsData);

      if (validClientsData.length > 0) {
        saveClientsToLocalStorage(validClientsData);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load clients',
        severity: 'error'
      });
    }
  };

  const fetchInvoices = async () => {
    if (!currentUser) return;

    try {
      const invoiceData = await indexedDBService.getAllItems<Invoice>('invoices');

      // Filter by usage for current user is already likely done by how we store/retrieve,
      // but if we store everything together we must filter.
      // Assuming 'invoices' store might contain everyone's, we filter by userId if property exists.
      // But indexedDBService putItem includes userId, so let's filter.

      const userInvoices = invoiceData.filter((inv: any) => inv.userId === currentUser.uid && inv.status !== 'deleted');

      // Sort by status and date
      userInvoices.sort((a, b) => {
        // Status sort order (if needed, but simple date sort usually better)
        // Using date desc
        return new Date(b.createdAt || b.date).getTime() - new Date(a.createdAt || a.date).getTime();
      });

      const mappedInvoices = userInvoices.map((inv: any) => {
        // Normalize client field to be a string
        let clientName = inv.client;
        if (typeof inv.client === 'object' && inv.client !== null) {
          clientName = inv.client.name || 'Unknown Client';
        }

        return {
          ...inv,
          client: clientName || inv.clientName || 'Unknown Client',
          amount: Number(inv.amount) || 0,
          subtotal: Number(inv.subtotal) || 0,
          tax: Number(inv.tax) || 0,
          discount: Number(inv.discount) || 0,
          items: (inv.items || []).map((item: any) => ({
            ...item,
            quantity: Number(item.quantity) || 0,
            rate: Number(item.rate) || 0,
            amount: Number(item.amount) || 0,
            gstRate: Number(item.gstRate) || 0
          }))
        };
      });
      setInvoices(mappedInvoices);
      calculateSummaryStats(mappedInvoices);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load invoices',
        severity: 'error'
      });
    }
  };

  const fetchInventoryItems = async () => {
    try {
      const items = await indexedDBService.getAllItems<InventoryItem>('inventory');
      setInventory(items);
    } catch (error) {
      console.error("Error fetching inventory", error);
    }
  };

  const calculateSummaryStats = (invoiceData: Invoice[]) => {
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

    const stats = {
      totalInvoices: 0,
      monthlyInvoices: 0,
      pendingAmount: 0,
      overdueAmount: 0
    };

    invoiceData.forEach(invoice => {
      // Total invoices value
      stats.totalInvoices += invoice.amount;

      // Monthly invoices value
      const invoiceDate = new Date(invoice.date);
      if (invoiceDate >= monthStart) {
        stats.monthlyInvoices += invoice.amount;
      }

      // Pending amount
      if (invoice.status === 'pending') {
        stats.pendingAmount += invoice.amount;
      }

      // Overdue amount
      if (invoice.status === 'overdue') {
        stats.overdueAmount += invoice.amount;
      }
    });

    setSummaryStats(stats);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleViewInvoice = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setViewInvoiceDialogOpen(true);
  };

  const handleEditInvoice = (invoiceId: string) => {
    // Instead of navigating to another page, we'll open the invoice in edit mode
    const invoiceToEdit = invoices.find(inv => inv.id === invoiceId);
    if (invoiceToEdit) {
      setSelectedInvoice(invoiceToEdit);
      setCreateInvoiceDialogOpen(true);
    }
  };

  const handleCloseViewInvoice = () => {
    setViewInvoiceDialogOpen(false);
  };

  const handleDeleteClick = (id: string) => {
    setDeleteInvoiceId(id);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteInvoiceId || !currentUser) return;

    try {
      setLoading(true);
      await InvoiceService.deleteInvoice(deleteInvoiceId, currentUser.uid);
      setSnackbar({
        open: true,
        message: 'Invoice deleted successfully',
        severity: 'success'
      });
      setDeleteConfirmOpen(false);
      setDeleteInvoiceId(null);
      await fetchInvoices();
    } catch (error) {
      console.error('Error deleting invoice:', error);
      setSnackbar({
        open: true,
        message: 'Failed to delete invoice',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenRecordPayment = (invoice: Invoice) => {
    setPaymentInvoice(invoice);
    setPaymentMode('cash');
    setPaymentDate(new Date().toISOString().split('T')[0]);
    setPaymentReference('');
    setSelectedBankIdx(0);
    setRecordPaymentOpen(true);
  };

  const handleSavePayment = async () => {
    if (!paymentInvoice || !currentUser) return;

    try {
      setLoading(true);
      
      let bankAccountDetails = { bank: '', accountNumber: '' };
      if (paymentMode === 'bank') {
        const userSettings = await SettingsService.fetchSettings(currentUser.uid);
        const businessProfile = (userSettings?.businessProfile || {}) as any;
        const bankAccounts = businessProfile.bankDetails ? [businessProfile.bankDetails] : [];
        const openingBalances = (userProfile as any)?.accountSettings?.openingBankBalances || [];
        const allAccounts = [...bankAccounts, ...openingBalances];
        const chosen = allAccounts[selectedBankIdx] || {};
        bankAccountDetails = {
          bank: chosen.bank || chosen.bankName || 'Default Bank',
          accountNumber: chosen.accountNumber || chosen.accNo || ''
        };
      }

      const updatedInvoice = {
        ...paymentInvoice,
        status: 'paid' as const,
        paymentDate,
        paymentInfo: {
          amountReceived: Number(paymentInvoice.amount) || 0,
          paymentMode,
          paymentDate,
          paymentReference,
          bankAccount: bankAccountDetails
        }
      };

      await InvoiceService.updateInvoice(paymentInvoice.id, updatedInvoice as any, currentUser.uid);
      
      setSnackbar({
        open: true,
        message: `Invoice #${paymentInvoice.invoiceNumber} marked as Paid successfully!`,
        severity: 'success'
      });
      
      setRecordPaymentOpen(false);
      await fetchInvoices();
    } catch (error) {
      console.error('Error saving payment:', error);
      setSnackbar({
        open: true,
        message: 'Failed to record payment.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNewInvoice = () => {
    // Check if business profile is complete before navigating
    if (!businessDetails || !businessDetails.name) {
      // Show dialog to update business profile
      setMissingBusinessFields(['name', 'address']);
      setOpenBusinessProfileDialog(true);
    } else {
      // Navigate to the new invoice creation page
      navigate('/invoices/new');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'success';
      case 'pending':
        return 'warning';
      case 'overdue':
        return 'error';
      case 'draft':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'paid':
        return <DoneAllIcon fontSize="small" />;
      case 'pending':
        return <HourglassEmptyIcon fontSize="small" />;
      case 'overdue':
        return <WarningIcon fontSize="small" />;
      case 'draft':
        return <ReceiptIcon fontSize="small" />;
      default:
        return <ReceiptIcon fontSize="small" />;
    }
  };

  // Filter invoices based on search, status
  const filteredInvoices = invoices.filter(invoice => {
    // Search filter
    const matchesSearch = searchQuery === '' ||
      invoice.invoiceNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
      invoice.client.toLowerCase().includes(searchQuery.toLowerCase());

    // Status filter
    const matchesStatus = statusFilter.length === 0 || statusFilter.includes(invoice.status);

    // Date filter (simplified - would need more complex logic for date ranges)
    const matchesDate = !dateFilter || invoice.date.includes(dateFilter);

    return matchesSearch && matchesStatus && matchesDate;
  });

  const allInvoicesFiltered = filteredInvoices.filter(invoice => invoice.status !== 'draft');

  const handleStatusFilterChange = (status: string) => {
    const currentIndex = statusFilter.indexOf(status);
    const newStatusFilter = [...statusFilter];

    if (currentIndex === -1) {
      newStatusFilter.push(status);
    } else {
      newStatusFilter.splice(currentIndex, 1);
    }

    setStatusFilter(newStatusFilter);
  };

  const handleSearchClick = (event: React.MouseEvent<HTMLElement>) => {
    setSearchAnchorEl(event.currentTarget);
  };

  const handleSearchClose = () => {
    setSearchAnchorEl(null);
  };

  const exportToCSV = () => {
    if (invoices.length === 0) return;

    const headers = ['Invoice Number', 'Client', 'Date', 'Due Date', 'Amount', 'Status'];

    const csvRows = [
      headers.join(','),
      ...invoices.map(invoice => {
        return [
          invoice.invoiceNumber || 'N/A',
          invoice.client || 'Unknown',
          invoice.date,
          invoice.dueDate,
          invoice.amount.toFixed(2),
          invoice.status
        ].join(',');
      })
    ];

    const csvContent = "data:text/csv;charset=utf-8," + csvRows.join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `invoices_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleOpenFilterMenu = (event: React.MouseEvent<HTMLElement>) => {
    setFilterMenuAnchor(event.currentTarget);
  };

  const handleCloseFilterMenu = () => {
    setFilterMenuAnchor(null);
  };

  const renderSummaryCards = () => (
    <Collapse in={showCards}>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              height: '100%',
              borderRadius: 4,
              background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.15) 0%, rgba(33, 203, 243, 0.05) 100%)',
              border: '1px solid rgba(33, 150, 243, 0.1)',
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.3s ease',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              '&:hover': {
                boxShadow: '0 8px 25px rgba(33, 150, 243, 0.15)',
                transform: 'translateY(-5px)',
              }
            }}
          >
            <Box display="flex" alignItems="center" mb={2}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(33, 150, 243, 0.2)',
                  color: '#2196F3',
                  mr: 2
                }}
              >
                <ReceiptIcon fontSize="small" />
              </Box>
              <Typography variant="subtitle1" fontWeight="600" color="text.primary">
                Total Invoices
              </Typography>
            </Box>

            <Typography variant="h5" fontWeight="bold" sx={{ mt: 2, color: '#2196F3' }}>
              ₹{summaryStats.totalInvoices.toFixed(2)}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              height: '100%',
              borderRadius: 4,
              background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.15) 0%, rgba(129, 199, 132, 0.05) 100%)',
              border: '1px solid rgba(76, 175, 80, 0.1)',
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.3s ease',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              '&:hover': {
                boxShadow: '0 8px 25px rgba(76, 175, 80, 0.15)',
                transform: 'translateY(-5px)',
              }
            }}
          >
            <Box display="flex" alignItems="center" mb={2}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(76, 175, 80, 0.2)',
                  color: '#4CAF50',
                  mr: 2
                }}
              >
                <CalendarTodayIcon fontSize="small" />
              </Box>
              <Typography variant="subtitle1" fontWeight="600" color="text.primary">
                Monthly Invoices
              </Typography>
            </Box>

            <Typography variant="h5" fontWeight="bold" sx={{ mt: 2, color: '#4CAF50' }}>
              ₹{summaryStats.monthlyInvoices.toFixed(2)}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              height: '100%',
              borderRadius: 4,
              background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.15) 0%, rgba(255, 183, 77, 0.05) 100%)',
              border: '1px solid rgba(255, 152, 0, 0.1)',
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.3s ease',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              '&:hover': {
                boxShadow: '0 8px 25px rgba(255, 152, 0, 0.15)',
                transform: 'translateY(-5px)',
              }
            }}
          >
            <Box display="flex" alignItems="center" mb={2}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(255, 152, 0, 0.2)',
                  color: '#FF9800',
                  mr: 2
                }}
              >
                <HourglassEmptyIcon fontSize="small" />
              </Box>
              <Typography variant="subtitle1" fontWeight="600" color="text.primary">
                Pending Amount
              </Typography>
            </Box>

            <Typography variant="h5" fontWeight="bold" sx={{ mt: 2, color: '#FF9800' }}>
              ₹{summaryStats.pendingAmount.toFixed(2)}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              height: '100%',
              borderRadius: 4,
              background: 'linear-gradient(135deg, rgba(244, 67, 54, 0.15) 0%, rgba(229, 115, 115, 0.05) 100%)',
              border: '1px solid rgba(244, 67, 54, 0.1)',
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.3s ease',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              '&:hover': {
                boxShadow: '0 8px 25px rgba(244, 67, 54, 0.15)',
                transform: 'translateY(-5px)',
              }
            }}
          >
            <Box display="flex" alignItems="center" mb={2}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(244, 67, 54, 0.2)',
                  color: '#F44336',
                  mr: 2
                }}
              >
                <WarningIcon fontSize="small" />
              </Box>
              <Typography variant="subtitle1" fontWeight="600" color="text.primary">
                Overdue Amount
              </Typography>
            </Box>

            <Typography variant="h5" fontWeight="bold" sx={{ mt: 2, color: '#F44336' }}>
              ₹{summaryStats.overdueAmount.toFixed(2)}
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Collapse>
  );

  // Add this function to handle closing the business profile dialog
  const handleCloseBusinessProfileDialog = () => {
    setOpenBusinessProfileDialog(false);
  };

  // Function to print the invoice
  const handlePrintInvoice = async () => {
    if (!selectedInvoice) return;

    // Fetch the full business details to ensure bank info and QR codes are included
    let printBusinessDetails = { ...businessDetails };
    if (currentUser) {
      try {
        const userSettings = await SettingsService.fetchSettings(currentUser.uid);
        if (userSettings && userSettings.businessProfile) {
          printBusinessDetails = {
            ...printBusinessDetails,
            ...userSettings.businessProfile
          };
        }
      } catch (e) {
        console.error('Error fetching business profile for printing:', e);
      }
    }

    // Create a temporary div to hold the invoice content
    const printContent = document.createElement('div');
    printContent.style.width = '210mm'; // A4 width
    printContent.style.padding = '20mm'; // 2cm margin
    printContent.style.fontFamily = 'Arial, sans-serif';
    printContent.style.fontSize = '12pt';

    const printBankName = printBusinessDetails?.bankName || printBusinessDetails?.bankDetails?.bankName || '';
    const printBankBranch = printBusinessDetails?.bankBranch || printBusinessDetails?.bankDetails?.branchName || '';
    const printAccountNumber = printBusinessDetails?.accountNumber || printBusinessDetails?.bankDetails?.accountNumber || '';
    const printIfscCode = printBusinessDetails?.ifscCode || printBusinessDetails?.ifsc || printBusinessDetails?.bankDetails?.ifscCode || '';
    const printUpiId = printBusinessDetails?.upiId || (printBusinessDetails as any)?.bankDetails?.upiId || '';
    const printUpiQrImage = printBusinessDetails?.upiQrImage || printBusinessDetails?.qrCode || (printUpiId ? `https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent(`upi://pay?pa=${printUpiId}&pn=${encodeURIComponent(printBusinessDetails?.name || printBusinessDetails?.businessName || 'Business')}&am=${selectedInvoice.amount || 0}&cu=INR`)}` : '');

    // Add the invoice content - properly formatted for printing
    printContent.innerHTML = `
      <style>
        @media print {
          body { margin: 0; padding: 0; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; margin-bottom: 20px; }
          th { background-color: #f3f3f3; text-align: left; padding: 8px; border-bottom: 1px solid #ddd; }
          td { padding: 8px; border-bottom: 1px solid #ddd; }
          .header { display: flex; justify-content: space-between; margin-bottom: 30px; }
          .invoice-title { font-size: 24pt; font-weight: bold; color: #2196F3; margin-bottom: 10px; }
          .section-title { font-size: 14pt; font-weight: bold; margin-top: 20px; margin-bottom: 10px; border-bottom: 1px solid #eee; padding-bottom: 5px; }
          .summary { margin-top: 20px; text-align: right; }
          .summary-table { width: 250px; margin-left: auto; }
          .summary-row { display: flex; justify-content: space-between; margin-bottom: 5px; }
          .total-row { font-weight: bold; border-top: 1px solid #ddd; padding-top: 5px; margin-top: 5px; }
          .terms { margin-top: 30px; border-top: 1px solid #ddd; padding-top: 10px; font-size: 10pt; }
          .footer { margin-top: 50px; font-size: 10pt; text-align: center; color: #666; }
        }
      </style>
      <div class="header">
        <div>
          <div class="invoice-title">INVOICE</div>
          <div style="font-size: 14pt; color: #666;">#${selectedInvoice.invoiceNumber}</div>
        </div>
        <div style="text-align: right;">
          <div style="font-weight: bold;">${printBusinessDetails?.name || 'Business Name'}</div>
          <div>${printBusinessDetails?.address || ''}</div>
          <div>${printBusinessDetails?.city || ''} ${printBusinessDetails?.pincode || ''}</div>
          <div>${printBusinessDetails?.phone ? `Phone: ${printBusinessDetails.phone}` : ''}</div>
          <div>${printBusinessDetails?.email || ''}</div>
          <div>${printBusinessDetails?.gstin ? `GSTIN: ${printBusinessDetails.gstin}` : ''}</div>
        </div>
      </div>

      <div style="display: flex; justify-content: space-between;">
        <div style="width: 45%;">
          <div class="section-title">Bill To</div>
          <div style="font-weight: bold;">${typeof selectedInvoice.client === 'object' ? (selectedInvoice.client as any)?.name : selectedInvoice.client}</div>
          <div>${selectedInvoice.clientDetails?.address || ''}</div>
          ${selectedInvoice.clientDetails?.gstin ? `<div>GSTIN: ${selectedInvoice.clientDetails.gstin}</div>` : ''}
          ${selectedInvoice.clientDetails?.phone ? `<div>Phone: ${selectedInvoice.clientDetails.phone}</div>` : ''}
          ${selectedInvoice.clientDetails?.email ? `<div>Email: ${selectedInvoice.clientDetails.email}</div>` : ''}
        </div>
        <div style="width: 45%; text-align: right;">
          <div class="section-title">Invoice Details</div>
          <div style="display: flex; justify-content: space-between;"><span>Invoice Date:</span> <span>${selectedInvoice.date}</span></div>
          <div style="display: flex; justify-content: space-between;"><span>Due Date:</span> <span>${selectedInvoice.dueDate}</span></div>
          <div style="display: flex; justify-content: space-between;"><span>Status:</span> <span>${selectedInvoice.status.toUpperCase()}</span></div>
        </div>
      </div>

      <div class="section-title">Items</div>
      <table>
        <thead>
          <tr>
            <th>Item</th>
            <th>Description</th>
            <th style="text-align: right;">Qty</th>
            <th style="text-align: right;">Rate</th>
            <th style="text-align: right;">Amount</th>
          </tr>
        </thead>
        <tbody>
          ${selectedInvoice.items.map(item => `
            <tr>
              <td>${item.name}</td>
              <td>${item.description}</td>
              <td style="text-align: right;">${item.quantity}</td>
              <td style="text-align: right;">₹${item.rate.toFixed(2)}</td>
              <td style="text-align: right;">₹${item.amount.toFixed(2)}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>

      <div class="summary">
        <div class="summary-table">
          <div class="summary-row">
            <span>Subtotal:</span>
            <span>₹${selectedInvoice.subtotal.toFixed(2)}</span>
          </div>
          ${selectedInvoice.tax > 0 ? `
            <div class="summary-row">
              <span>Tax:</span>
              <span>₹${selectedInvoice.tax.toFixed(2)}</span>
            </div>
          ` : ''}
          ${selectedInvoice.discount > 0 ? `
            <div class="summary-row">
              <span>Discount:</span>
              <span>₹${selectedInvoice.discount.toFixed(2)}</span>
            </div>
          ` : ''}
          <div class="summary-row total-row">
            <span>Total:</span>
            <span>₹${selectedInvoice.amount.toFixed(2)}</span>
          </div>
        </div>
      </div>

      ${(printBankName || printUpiId) ? `
        <div class="section-title">Payment Details</div>
        <div style="display: flex; gap: 20px; align-items: flex-start; margin-bottom: 20px;">
          <div style="flex: 1;">
            ${printBankName ? `<div><strong>Bank:</strong> ${printBankName}</div>` : ''}
            ${printBankBranch ? `<div><strong>Branch:</strong> ${printBankBranch}</div>` : ''}
            ${printAccountNumber ? `<div><strong>Account Number:</strong> ${printAccountNumber}</div>` : ''}
            ${printIfscCode ? `<div><strong>IFSC Code:</strong> ${printIfscCode}</div>` : ''}
            ${printUpiId ? `<div><strong>UPI ID:</strong> ${printUpiId}</div>` : ''}
          </div>
          ${printUpiQrImage ? `
            <div>
              <img src="${printUpiQrImage}" alt="UPI QR Code" style="max-width: 120px; max-height: 120px;" />
            </div>
          ` : ''}
        </div>
      ` : ''}

      ${selectedInvoice.notes ? `
        <div class="section-title">Notes</div>
        <div>${selectedInvoice.notes}</div>
      ` : ''}

      ${selectedInvoice.termsAndConditions ? `
        <div class="section-title">Terms & Conditions</div>
        <div>${selectedInvoice.termsAndConditions}</div>
      ` : ''}

      <div class="terms">
        <p>Payment is due within 15 days from the date of invoice. Late payments may be subject to interest charges.</p>
        <p>All disputes are subject to local jurisdiction.</p>
      </div>

      <div class="footer">
        Thank you for your business!
      </div>
    `;

    const htmlContent = `
      <html>
        <head>
          <title>Invoice #${selectedInvoice.invoiceNumber}</title>
        </head>
        <body>
          ${printContent.outerHTML}
        </body>
      </html>
    `;

    // Use a hidden iframe instead of a new window to prevent freezing the parent tab
    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.width = '0px';
    iframe.style.height = '0px';
    iframe.style.border = 'none';
    document.body.appendChild(iframe);

    const doc = iframe.contentWindow?.document;
    if (doc) {
      doc.open();
      doc.write(htmlContent);
      doc.close();

      // Wait for content to render before printing
      setTimeout(() => {
        if (iframe.contentWindow) {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        }
        // Clean up
        setTimeout(() => {
          if (document.body.contains(iframe)) {
            document.body.removeChild(iframe);
          }
        }, 1000);
      }, 500);
    }
  };

  // Function to get payment status label
  const getPaymentStatusLabel = (status: string): string => {
    const normalizedStatus = (status || '').toLowerCase();
    switch (normalizedStatus) {
      case 'paid':
        return 'Payment Received';
      case 'pending':
        return 'Payment Pending';
      case 'overdue':
        return 'Payment Overdue';
      case 'draft':
        return 'Draft';
      case 'deleted':
        return 'Deleted';
      default:
        return status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown';
    }
  };

  // Function for thermal printing
  const handleThermalPrint = () => {
    if (!selectedInvoice) return;

    // Create a temporary div to hold the thermal receipt content
    const printContent = document.createElement('div');
    printContent.style.width = '80mm'; // Standard thermal receipt width
    printContent.style.fontFamily = 'monospace';
    printContent.style.fontSize = '10pt';

    // Add the receipt content - formatted for thermal printer
    printContent.innerHTML = `
      <style>
        @media print {
          body { margin: 0; padding: 0; font-family: monospace; font-size: 10pt; }
          .center { text-align: center; }
          .divider { border-bottom: 1px dashed #000; margin: 5px 0; }
          .receipt-item { display: flex; justify-content: space-between; margin: 2px 0; }
          .column-70 { width: 70%; }
          .column-30 { width: 30%; text-align: right; }
        }
      </style>
      <div class="center">
        <div style="font-weight: bold; font-size: 12pt;">${businessDetails?.name || 'Business Name'}</div>
        <div>${businessDetails?.address || ''}</div>
        <div>${businessDetails?.city || ''} ${businessDetails?.pincode || ''}</div>
        <div>${businessDetails?.phone ? `Phone: ${businessDetails.phone}` : ''}</div>
        <div>${businessDetails?.gstin ? `GSTIN: ${businessDetails.gstin}` : ''}</div>
      </div>
      
      <div class="divider"></div>
      
      <div class="center">
        <div style="font-weight: bold;">INVOICE #${selectedInvoice.invoiceNumber}</div>
        <div>Date: ${selectedInvoice.date}</div>
      </div>
      
      <div class="divider"></div>
      
      <div>
        <div>Customer: ${typeof selectedInvoice.client === 'object' ? (selectedInvoice.client as any)?.name : selectedInvoice.client}</div>
        ${selectedInvoice.clientDetails?.phone ? `<div>Phone: ${selectedInvoice.clientDetails.phone}</div>` : ''}
        ${selectedInvoice.clientDetails?.gstin ? `<div>GSTIN: ${selectedInvoice.clientDetails.gstin}</div>` : ''}
      </div>
      
      <div class="divider"></div>
      
      <div style="font-weight: bold;" class="receipt-item">
        <span class="column-70">Item</span>
        <span class="column-30">Amount</span>
      </div>
      <div class="divider"></div>
      
      ${selectedInvoice.items.map(item => `
        <div class="receipt-item">
          <div class="column-70">
            ${item.name}<br>
            ${item.quantity} x ₹${item.rate.toFixed(2)}
          </div>
          <div class="column-30">₹${item.amount.toFixed(2)}</div>
        </div>
      `).join('<div class="divider"></div>')}
      
      <div class="divider"></div>
      
      <div class="receipt-item">
        <span class="column-70">Subtotal:</span>
        <span class="column-30">₹${selectedInvoice.subtotal.toFixed(2)}</span>
      </div>
      ${selectedInvoice.tax > 0 ? `
        <div class="receipt-item">
          <span class="column-70">Tax:</span>
          <span class="column-30">₹${selectedInvoice.tax.toFixed(2)}</span>
        </div>
      ` : ''}
      ${selectedInvoice.discount > 0 ? `
        <div class="receipt-item">
          <span class="column-70">Discount:</span>
          <span class="column-30">-₹${selectedInvoice.discount.toFixed(2)}</span>
        </div>
      ` : ''}
      <div class="receipt-item" style="font-weight: bold; font-size: 12pt;">
        <span class="column-70">TOTAL:</span>
        <span class="column-30">₹${selectedInvoice.amount.toFixed(2)}</span>
      </div>
      
      <div class="divider"></div>
      
      <div class="center" style="margin-top: 10px;">
        <div>Payment Status: ${selectedInvoice.status.toUpperCase()}</div>
        <div style="margin-top: 10px;">Thank you for your business!</div>
      </div>
    `;

    const htmlContent = `
      <html>
        <head>
          <title>Receipt #${selectedInvoice.invoiceNumber}</title>
        </head>
        <body>
          ${printContent.outerHTML}
        </body>
      </html>
    `;

    // Use a hidden iframe instead of a new window to prevent freezing the parent tab
    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.width = '0px';
    iframe.style.height = '0px';
    iframe.style.border = 'none';
    document.body.appendChild(iframe);

    const doc = iframe.contentWindow?.document;
    if (doc) {
      doc.open();
      doc.write(htmlContent);
      doc.close();

      // Wait for content to render before printing
      setTimeout(() => {
        if (iframe.contentWindow) {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        }
        // Clean up
        setTimeout(() => {
          if (document.body.contains(iframe)) {
            document.body.removeChild(iframe);
          }
        }, 1000);
      }, 500);
    }
  };

  // Function to email the PDF
  const handleEmailPDF = async () => {
    if (!selectedInvoice) return;

    try {
      setLoading(true);

      let profileDetails = { ...businessDetails } as any;
      let subject = `Invoice ${selectedInvoice.invoiceNumber} from ${businessDetails?.name || 'Business'}`;
      let body = `Dear ${selectedInvoice.client},\n\nPlease find attached the invoice ${selectedInvoice.invoiceNumber} for your recent purchase.\n\nTotal Amount: ${new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(selectedInvoice.amount)}\nDue Date: ${selectedInvoice.dueDate}\n\nThank you for your business.\n\nRegards,\n${businessDetails?.name || 'Business'}`;

      if (currentUser) {
        try {
          const userSettings = await SettingsService.fetchSettings(currentUser.uid);
          if (userSettings) {
            // Enrich brand details
            if (userSettings.businessProfile) {
              profileDetails = {
                ...profileDetails,
                ...userSettings.businessProfile,
                logoUrl: userSettings.businessProfile.logoUrl,
                primaryColor: userSettings.businessProfile.primaryColor,
                secondaryColor: userSettings.businessProfile.secondaryColor,
              };
            }

            // Parse email template
            if (userSettings.invoiceSettings) {
              const amountFormatted = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(selectedInvoice.amount);
              const templateData = {
                customer_name: selectedInvoice.client,
                invoice_number: selectedInvoice.invoiceNumber,
                amount_due: amountFormatted,
                due_date: selectedInvoice.dueDate,
                business_name: businessDetails?.name || 'Business',
              };

              const parseTemplate = (tmpl: string) => {
                return tmpl
                  .replace(/\{\{customer_name\}\}/g, templateData.customer_name)
                  .replace(/\{clientName\}/g, templateData.customer_name)
                  .replace(/\{\{invoice_number\}\}/g, templateData.invoice_number)
                  .replace(/\{invoiceNumber\}/g, templateData.invoice_number)
                  .replace(/\{\{amount_due\}\}/g, templateData.amount_due)
                  .replace(/\{amount\}/g, templateData.amount_due)
                  .replace(/\{\{due_date\}\}/g, templateData.due_date)
                  .replace(/\{dueDate\}/g, templateData.due_date)
                  .replace(/\{\{business_name\}\}/g, templateData.business_name)
                  .replace(/\{businessName\}/g, templateData.business_name);
              };

              if (userSettings.invoiceSettings.emailSubject) {
                subject = parseTemplate(userSettings.invoiceSettings.emailSubject);
              }
              if (userSettings.invoiceSettings.emailBody) {
                body = parseTemplate(userSettings.invoiceSettings.emailBody);
              }
            }
          }
        } catch (e) {
          console.error('Error fetching brand/template settings:', e);
        }
      }

      // First get the PDF as a blob
      const pdfBytes = await generateInvoicePDF(
        selectedInvoice,
        profileDetails,
        1 // Default template ID
      );

      // Display a dialog or send directly if we have the user's email
      if (selectedInvoice.clientDetails?.email) {
        setEmailSubject(subject);
        setEmailBody(body);
        setEmailTo(selectedInvoice.clientDetails.email);
        setEmailPdfBytes(pdfBytes);
        setEmailDialogOpen(true);
      } else {
        setSnackbar({
          open: true,
          message: 'Client email not available',
          severity: 'warning'
        });
      }
    } catch (error) {
      console.error('Error preparing email:', error);
      setSnackbar({
        open: true,
        message: 'Failed to prepare invoice for email',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmailConfirm = async () => {
    if (!selectedInvoice || !emailPdfBytes) return;

    setEmailSending(true);
    let emailSent = false;
    
    // Try to fetch SMTP settings and use the backend service first
    if (currentUser) {
      try {
        const userSettings = await SettingsService.fetchSettings(currentUser.uid);
        if (userSettings && userSettings.smtpSettings && userSettings.smtpSettings.host) {
            await EmailService.sendInvoiceEmail(
              emailTo,
              emailSubject,
              emailBody.replace(/\n/g, '<br/>'),
              emailPdfBytes,
              `Invoice_${selectedInvoice.invoiceNumber}.pdf`,
              userSettings.smtpSettings
            );
            emailSent = true;
            setSnackbar({
              open: true,
              message: 'Invoice emailed successfully via configured SMTP!',
              severity: 'success'
            });
            setEmailDialogOpen(false);
        }
      } catch (e) {
        console.error('SMTP sending failed, falling back to local client', e);
      }
    }

    // Fallback to mailto if SMTP wasn't configured or failed
    if (!emailSent) {
      // Convert Uint8Array to Blob for download fallback
      const pdfBlob = new Blob([emailPdfBytes], { type: 'application/pdf' });
      
      window.open(`mailto:${emailTo}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`);

      setSnackbar({
        open: true,
        message: 'Email client opened. Please attach the downloaded PDF.',
        severity: 'info'
      });

      // Also download the PDF since we can't attach it via mailto
      const link = document.createElement('a');
      link.href = URL.createObjectURL(pdfBlob);
      link.download = `Invoice_${selectedInvoice.invoiceNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setEmailDialogOpen(false);
    }
    
    setEmailSending(false);
  };

  // Function to view the next invoice
  const handleViewNextInvoice = () => {
    if (!selectedInvoice || filteredInvoices.length <= 1) return;

    // Find the index of the current invoice
    const currentIndex = filteredInvoices.findIndex(inv => inv.id === selectedInvoice.id);

    // Get the next invoice, wrapping around to the first if at the end
    const nextIndex = (currentIndex + 1) % filteredInvoices.length;
    const nextInvoice = filteredInvoices[nextIndex];

    // Set as the selected invoice
    setSelectedInvoice(nextInvoice);
  };

  // Handle close of create invoice dialog
  const handleCloseCreateInvoiceDialog = () => {
    setCreateInvoiceDialogOpen(false);
    // Refresh invoices after creation
    fetchInvoices();
  };



  // Function to save the invoice as PDF
  const handleSavePDF = async () => {
    if (!selectedInvoice) return;

    try {
      setLoading(true);

      // Generate the PDF with the AkMo template
      const pdfBytes = await generateInvoicePDF(
        selectedInvoice,
        businessDetails || { name: 'Business' },
        1 // Default template ID
      );

      // Convert Uint8Array to Blob
      const pdfBlob = new Blob([pdfBytes], { type: 'application/pdf' });

      // Create a URL for the blob
      const url = URL.createObjectURL(pdfBlob);

      // Create a link element for download
      const link = document.createElement('a');
      link.href = url;
      link.download = `Invoice_${selectedInvoice.invoiceNumber.replace(/[\/\\:*?"<>|]/g, '_')}.pdf`;

      // Append to the document and click
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setSnackbar({
        open: true,
        message: 'PDF saved successfully',
        severity: 'success'
      });

    } catch (error) {
      console.error('Error generating PDF:', error);
      setSnackbar({
        open: true,
        message: 'Failed to generate PDF. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Add the navigateToClientsPage function since it's missing
  const navigateToClientsPage = () => {
    navigate('/clients');
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <Box mb={4} display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h4" fontWeight="bold" sx={{
          background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}>
          Invoice Management
        </Typography>

        <Box display="flex" gap={2}>
          <Tooltip title="Search Invoices">
            <IconButton color="primary" onClick={handleSearchClick}>
              <SearchIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Filter Invoices">
            <IconButton color="primary" onClick={handleOpenFilterMenu}>
              <FilterListIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Export to CSV">
            <IconButton
              color="primary"
              onClick={exportToCSV}
              disabled={invoices.length === 0 || loading}
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title={showCards ? "Hide Summary Cards" : "Show Summary Cards"}>
            <IconButton onClick={() => setShowCards(!showCards)} color="primary">
              {showCards ? <VisibilityOffIcon /> : <VisibilityIcon />}
            </IconButton>
          </Tooltip>

          <Button
            variant="contained"
            onClick={() => handleCreateNewInvoice()}
            startIcon={<AddIcon />}
            sx={{ ml: 2, bgcolor: 'primary.main' }}
          >
            Create Invoice
          </Button>
        </Box>
      </Box>

      {renderSummaryCards()}

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <CircularProgress />
          <Typography variant="body1" sx={{ ml: 2 }}>
            Loading invoices...
          </Typography>
        </Box>
      ) : (
        <>
          {(!businessDetails || !businessDetails.name) && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              Business profile is incomplete or missing. Please update your business profile for accurate invoicing.
            </Alert>
          )}

          {/* Only show this alert if we're certain there are no clients and not loading */}
          {!loading && clients?.length === 0 && (
            <Alert
              severity="info"
              sx={{ mb: 3 }}
              action={
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    color="info"
                    size="small"
                    onClick={() => fetchClients()}
                    startIcon={<RefreshIcon />}
                  >
                    Refresh
                  </Button>
                  <Button
                    color="info"
                    size="small"
                    onClick={navigateToClientsPage}
                    variant="outlined"
                  >
                    Add Client
                  </Button>
                </Box>
              }
            >
              No clients found. Add clients to create invoices for them.
            </Alert>
          )}

          <Popover
            open={Boolean(searchAnchorEl)}
            anchorEl={searchAnchorEl}
            onClose={handleSearchClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <Box sx={{ p: 2, width: 300 }}>
              <FormControl variant="outlined" size="small" sx={{ width: '100%' }}>
                <OutlinedInput
                  placeholder="Search Invoice..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                  startAdornment={
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  }
                />
              </FormControl>
            </Box>
          </Popover>

          {/* Status filter menu */}
          <Menu
            anchorEl={filterMenuAnchor}
            open={Boolean(filterMenuAnchor)}
            onClose={handleCloseFilterMenu}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Status
              </Typography>
              <Box display="flex" flexDirection="column">
                {['draft', 'pending', 'paid', 'overdue'].map((status) => (
                  <FormControlLabel
                    key={status}
                    control={
                      <Switch
                        checked={statusFilter.indexOf(status) !== -1}
                        onChange={() => handleStatusFilterChange(status)}
                        size="small"
                      />
                    }
                    label={status.charAt(0).toUpperCase() + status.slice(1)}
                  />
                ))}
              </Box>
            </Box>
          </Menu>

          {/* Status tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              textColor="primary"
              indicatorColor="primary"
              variant="fullWidth"
            >
              <Tab label="All Invoices" />
              <Tab label="Draft" />
              <Tab label="Pending" />
              <Tab label="Paid" />
              <Tab label="Overdue" />
            </Tabs>
          </Box>

          {/* Tab panels with data table */}
          <TabPanel value={tabValue} index={0}>
            <TableContainer component={Paper} elevation={0} sx={{
              borderRadius: 3,
              boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)',
              overflowX: { xs: 'auto', md: 'hidden' },
              width: '100%'
            }}>
              <Table sx={{ minWidth: '100%' }} size="small">
                <TableHead>
                  <TableRow sx={{ background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)' }}>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', minWidth: '100px', fontWeight: 'bold' }}>Invoice #</TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', minWidth: '120px', fontWeight: 'bold' }}>Client</TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', minWidth: '100px', fontWeight: 'bold' }}>Date</TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', minWidth: '100px', fontWeight: 'bold' }}>Due Date</TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', minWidth: '100px', fontWeight: 'bold' }}>Amount</TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', minWidth: '100px', fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', minWidth: '100px', fontWeight: 'bold' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {allInvoicesFiltered
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((invoice) => (
                      <TableRow key={invoice.id} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{invoice.invoiceNumber}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{invoice.client}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{invoice.date}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{invoice.dueDate}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>₹{invoice.amount.toFixed(2)}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Chip
                            icon={getStatusIcon(invoice.status)}
                            label={invoice.status.toUpperCase()}
                            color={getStatusColor(invoice.status) as any}
                            size="small"
                            sx={{ fontWeight: 500 }}
                          />
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Box sx={{ display: 'flex' }}>
                            <IconButton size="small" onClick={() => handleViewInvoice(invoice)} title="View Invoice">
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleEditInvoice(invoice.id)} title="Edit Invoice">
                              <EditIcon fontSize="small" />
                            </IconButton>
                            {(invoice.status === 'pending' || invoice.status === 'overdue') && (
                              <IconButton size="small" onClick={() => handleOpenRecordPayment(invoice)} title="Mark as Paid" color="success">
                                <PaymentsIcon fontSize="small" />
                              </IconButton>
                            )}
                            {invoice.status !== 'paid' && (
                              <IconButton size="small" onClick={() => handleDeleteClick(invoice.id)} title="Delete Invoice">
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  {allInvoicesFiltered.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                        <Typography variant="subtitle1" color="text.secondary">
                          No invoices found matching your criteria
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={allInvoicesFiltered.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(event, newPage) => setPage(newPage)}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
              sx={{ mt: 2 }}
            />
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <TableContainer component={Paper} elevation={0} sx={{
              borderRadius: 3,
              boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)',
              overflowX: { xs: 'auto', md: 'hidden' },
              width: '100%'
            }}>
              <Table sx={{ minWidth: '100%' }} size="small">
                <TableHead>
                  <TableRow sx={{ background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)' }}>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', minWidth: '100px', fontWeight: 'bold' }}>Invoice #</TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', minWidth: '120px', fontWeight: 'bold' }}>Client</TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', minWidth: '100px', fontWeight: 'bold' }}>Date</TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', minWidth: '100px', fontWeight: 'bold' }}>Due Date</TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', minWidth: '100px', fontWeight: 'bold' }}>Amount</TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', minWidth: '100px', fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', minWidth: '100px', fontWeight: 'bold' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredInvoices
                    .filter(inv => inv.status === 'draft')
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((invoice) => (
                      <TableRow key={invoice.id} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{invoice.invoiceNumber}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{invoice.client}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{invoice.date}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{invoice.dueDate}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>₹{invoice.amount.toFixed(2)}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Chip
                            icon={getStatusIcon(invoice.status)}
                            label={invoice.status.toUpperCase()}
                            color={getStatusColor(invoice.status) as any}
                            size="small"
                            sx={{ fontWeight: 500 }}
                          />
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Box sx={{ display: 'flex' }}>
                            <IconButton size="small" onClick={() => handleViewInvoice(invoice)} title="View Invoice">
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleEditInvoice(invoice.id)} title="Edit Invoice">
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleDeleteClick(invoice.id)} title="Delete Invoice">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  {filteredInvoices.filter(inv => inv.status === 'draft').length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                        <Typography variant="subtitle1" color="text.secondary">
                          No draft invoices found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <TableContainer component={Paper} elevation={0} sx={{
              borderRadius: 3,
              boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)',
              overflowX: { xs: 'auto', md: 'hidden' },
              width: '100%'
            }}>
              <Table sx={{ minWidth: '100%' }} size="small">
                <TableHead>
                  <TableRow sx={{ background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)' }}>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', minWidth: '100px', fontWeight: 'bold' }}>Invoice #</TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', minWidth: '120px', fontWeight: 'bold' }}>Client</TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', minWidth: '100px', fontWeight: 'bold' }}>Date</TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', minWidth: '100px', fontWeight: 'bold' }}>Due Date</TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', minWidth: '100px', fontWeight: 'bold' }}>Amount</TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', minWidth: '100px', fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', minWidth: '100px', fontWeight: 'bold' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredInvoices
                    .filter(inv => inv.status === 'pending')
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((invoice) => (
                      <TableRow key={invoice.id} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{invoice.invoiceNumber}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{invoice.client}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{invoice.date}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{invoice.dueDate}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>₹{invoice.amount.toFixed(2)}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Chip
                            icon={getStatusIcon(invoice.status)}
                            label={invoice.status.toUpperCase()}
                            color={getStatusColor(invoice.status) as any}
                            size="small"
                            sx={{ fontWeight: 500 }}
                          />
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Box sx={{ display: 'flex' }}>
                            <IconButton size="small" onClick={() => handleViewInvoice(invoice)} title="View Invoice">
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleEditInvoice(invoice.id)} title="Edit Invoice">
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleOpenRecordPayment(invoice)} title="Mark as Paid" color="success">
                              <PaymentsIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleDeleteClick(invoice.id)} title="Delete Invoice">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  {filteredInvoices.filter(inv => inv.status === 'pending').length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                        <Typography variant="subtitle1" color="text.secondary">
                          No pending invoices found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <TableContainer component={Paper} elevation={0} sx={{
              borderRadius: 3,
              boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)',
              overflowX: { xs: 'auto', md: 'hidden' },
              width: '100%'
            }}>
              <Table sx={{ minWidth: '100%' }} size="small">
                <TableHead>
                  <TableRow sx={{ background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)' }}>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', minWidth: '100px', fontWeight: 'bold' }}>Invoice #</TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', minWidth: '120px', fontWeight: 'bold' }}>Client</TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', minWidth: '100px', fontWeight: 'bold' }}>Date</TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', minWidth: '100px', fontWeight: 'bold' }}>Due Date</TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', minWidth: '100px', fontWeight: 'bold' }}>Amount</TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', minWidth: '100px', fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', minWidth: '100px', fontWeight: 'bold' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredInvoices
                    .filter(inv => inv.status === 'paid')
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((invoice) => (
                      <TableRow key={invoice.id} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{invoice.invoiceNumber}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{invoice.client}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{invoice.date}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{invoice.dueDate}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>₹{invoice.amount.toFixed(2)}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Chip
                            icon={getStatusIcon(invoice.status)}
                            label={invoice.status.toUpperCase()}
                            color={getStatusColor(invoice.status) as any}
                            size="small"
                            sx={{ fontWeight: 500 }}
                          />
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Box sx={{ display: 'flex' }}>
                            <IconButton size="small" onClick={() => handleViewInvoice(invoice)} title="View Invoice">
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleEditInvoice(invoice.id)} title="Edit Invoice">
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  {filteredInvoices.filter(inv => inv.status === 'paid').length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                        <Typography variant="subtitle1" color="text.secondary">
                          No paid invoices found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          <TabPanel value={tabValue} index={4}>
            <TableContainer component={Paper} elevation={0} sx={{
              borderRadius: 3,
              boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)',
              overflowX: { xs: 'auto', md: 'hidden' },
              width: '100%'
            }}>
              <Table sx={{ minWidth: '100%' }} size="small">
                <TableHead>
                  <TableRow sx={{ background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)' }}>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', minWidth: '100px', fontWeight: 'bold' }}>Invoice #</TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', minWidth: '120px', fontWeight: 'bold' }}>Client</TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', minWidth: '100px', fontWeight: 'bold' }}>Date</TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', minWidth: '100px', fontWeight: 'bold' }}>Due Date</TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', minWidth: '100px', fontWeight: 'bold' }}>Amount</TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', minWidth: '100px', fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', minWidth: '100px', fontWeight: 'bold' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredInvoices
                    .filter(inv => inv.status === 'overdue')
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((invoice) => (
                      <TableRow key={invoice.id} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{invoice.invoiceNumber}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{invoice.client}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{invoice.date}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{invoice.dueDate}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>₹{invoice.amount.toFixed(2)}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Chip
                            icon={getStatusIcon(invoice.status)}
                            label={invoice.status.toUpperCase()}
                            color={getStatusColor(invoice.status) as any}
                            size="small"
                            sx={{ fontWeight: 500 }}
                          />
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Box sx={{ display: 'flex' }}>
                            <IconButton size="small" onClick={() => handleViewInvoice(invoice)} title="View Invoice">
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleEditInvoice(invoice.id)} title="Edit Invoice">
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleOpenRecordPayment(invoice)} title="Mark as Paid" color="success">
                              <PaymentsIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleDeleteClick(invoice.id)} title="Delete Invoice">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  {filteredInvoices.filter(inv => inv.status === 'overdue').length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                        <Typography variant="subtitle1" color="text.secondary">
                          No overdue invoices found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>
        </>
      )}

      {/* View Invoice Dialog */}
      <Dialog
        open={viewInvoiceDialogOpen}
        onClose={handleCloseViewInvoice}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Invoice #{selectedInvoice?.invoiceNumber}</Typography>
            <IconButton
              aria-label="close"
              onClick={handleCloseViewInvoice}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pb: 2 }}>
          {selectedInvoice && (
            <Box sx={{ p: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body1" fontWeight="500">Client</Typography>
                  <Typography variant="body2">{selectedInvoice.client}</Typography>
                  {selectedInvoice.clientDetails?.address && (
                    <Typography variant="body2">{selectedInvoice.clientDetails.address}</Typography>
                  )}
                  {selectedInvoice.clientDetails?.gstin && (
                    <Typography variant="body2">GSTIN: {selectedInvoice.clientDetails.gstin}</Typography>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="body1" fontWeight="500">Invoice Details</Typography>
                    <Typography variant="body2">Date: {selectedInvoice.date}</Typography>
                    <Typography variant="body2">Due Date: {selectedInvoice.dueDate}</Typography>
                    <Typography variant="body2">
                      Status:
                      <Chip
                        label={selectedInvoice.status.toUpperCase()}
                        color={getStatusColor(selectedInvoice.status) as any}
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" sx={{ mb: 1 }}>Items</Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Item</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell align="right">Qty</TableCell>
                      <TableCell align="right">Rate</TableCell>
                      <TableCell align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedInvoice.items.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>{item.name}</TableCell>
                        <TableCell>{item.description}</TableCell>
                        <TableCell align="right">{item.quantity}</TableCell>
                        <TableCell align="right">₹{item.rate.toFixed(2)}</TableCell>
                        <TableCell align="right">₹{item.amount.toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Box sx={{ width: '250px', textAlign: 'right' }}>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography variant="body2">Subtotal:</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">₹{selectedInvoice.subtotal.toFixed(2)}</Typography>
                    </Grid>

                    {selectedInvoice.tax > 0 && (
                      <>
                        <Grid item xs={6}>
                          <Typography variant="body2">Tax:</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2">₹{selectedInvoice.tax.toFixed(2)}</Typography>
                        </Grid>
                      </>
                    )}

                    {selectedInvoice.discount > 0 && (
                      <>
                        <Grid item xs={6}>
                          <Typography variant="body2">Discount:</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2">₹{selectedInvoice.discount.toFixed(2)}</Typography>
                        </Grid>
                      </>
                    )}

                    <Grid item xs={6}>
                      <Typography variant="body1" fontWeight="500">Total:</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body1" fontWeight="500">₹{selectedInvoice.amount.toFixed(2)}</Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Box>

              {selectedInvoice.notes && (
                <>
                  <Typography variant="body1" fontWeight="500">Notes</Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>{selectedInvoice.notes}</Typography>
                </>
              )}

              {selectedInvoice.termsAndConditions && (
                <>
                  <Typography variant="body1" fontWeight="500">Terms & Conditions</Typography>
                  <Typography variant="body2">{selectedInvoice.termsAndConditions}</Typography>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseViewInvoice} variant="outlined" sx={{ borderRadius: 1 }}>
            Close
          </Button>
          <Button
            startIcon={<PrintIcon />}
            variant="outlined"
            sx={{ borderRadius: 1 }}
            onClick={handlePrintInvoice}
          >
            Print
          </Button>
          <Button
            startIcon={<PrintIcon />}
            variant="outlined"
            sx={{ borderRadius: 1 }}
            onClick={handleThermalPrint}
          >
            Thermal Print
          </Button>
          <Button
            startIcon={<DownloadIcon />}
            variant="outlined"
            sx={{ borderRadius: 1 }}
            onClick={handleSavePDF}
          >
            Save PDF
          </Button>
          <Button
            startIcon={<EmailIcon />}
            variant="outlined"
            sx={{ borderRadius: 1 }}
            onClick={handleEmailPDF}
          >
            Email PDF
          </Button>
          <Button
            startIcon={<ArrowForwardIcon />}
            variant="outlined"
            color="primary"
            onClick={handleViewNextInvoice}
            sx={{ borderRadius: 1 }}
          >
            View Next
          </Button>
          {selectedInvoice && (selectedInvoice.status === 'pending' || selectedInvoice.status === 'overdue') && (
            <Button
              startIcon={<DoneAllIcon />}
              variant="contained"
              color="success"
              onClick={() => {
                handleCloseViewInvoice();
                handleOpenRecordPayment(selectedInvoice);
              }}
              sx={{ borderRadius: 1 }}
            >
              Mark as Paid
            </Button>
          )}
          <Button
            startIcon={<EditIcon />}
            variant="contained"
            color="primary"
            onClick={() => {
              handleCloseViewInvoice();
              if (selectedInvoice) {
                handleEditInvoice(selectedInvoice.id);
              }
            }}
            sx={{
              borderRadius: 1,
              backgroundImage: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)'
            }}
          >
            Edit
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Add the BusinessProfileUpdateDialog at the end of the component, before the closing tag */}
      <BusinessProfileUpdateDialog
        open={openBusinessProfileDialog}
        onClose={handleCloseBusinessProfileDialog}
        missingFields={missingBusinessFields}
      />

      {/* Create Invoice Dialog */}
      <CreateInvoiceDialog
        open={createInvoiceDialogOpen}
        onClose={handleCloseCreateInvoiceDialog}
        clients={clients}
        inventory={inventory}
        businessDetails={businessDetails}
      />
      {/* Email Edit Dialog */}
      <Dialog open={emailDialogOpen} onClose={() => !emailSending && setEmailDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Compose Email</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            margin="dense"
            label="To"
            value={emailTo}
            onChange={(e) => setEmailTo(e.target.value)}
            disabled={emailSending}
          />
          <TextField
            fullWidth
            margin="dense"
            label="Subject"
            value={emailSubject}
            onChange={(e) => setEmailSubject(e.target.value)}
            disabled={emailSending}
          />
          <TextField
            fullWidth
            margin="dense"
            label="Message"
            multiline
            rows={6}
            value={emailBody}
            onChange={(e) => setEmailBody(e.target.value)}
            disabled={emailSending}
          />

          <Box mt={3}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Attachments
            </Typography>
            <Box 
              sx={{ 
                border: '1px solid', 
                borderColor: 'divider', 
                borderRadius: 1, 
                p: 1.5, 
                display: 'flex', 
                alignItems: 'center',
                bgcolor: 'background.default'
              }}
            >
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  bgcolor: 'error.light',
                  color: 'error.main',
                  borderRadius: 1,
                  p: 1,
                  mr: 2
                }}
              >
                <PdfIcon />
              </Box>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body2" fontWeight="medium">
                  Invoice_{selectedInvoice?.invoiceNumber}.pdf
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  PDF Document {emailPdfBytes ? `• ${(emailPdfBytes.length / 1024).toFixed(1)} KB` : ''}
                </Typography>
              </Box>
              <Chip size="small" icon={<AttachFileIcon fontSize="small" />} label="Attached" color="primary" variant="outlined" />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailDialogOpen(false)} disabled={emailSending}>
            Cancel
          </Button>
          <Button 
            onClick={handleSendEmailConfirm} 
            variant="contained" 
            color="primary"
            disabled={emailSending || !emailTo || !emailSubject}
            startIcon={emailSending ? <CircularProgress size={20} color="inherit" /> : <EmailIcon />}
          >
            {emailSending ? 'Sending...' : 'Send Email'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Record Payment Dialog */}
      <Dialog open={recordPaymentOpen} onClose={() => setRecordPaymentOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Record Payment</DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={2}>
            {paymentInvoice && (
              <Box p={2} sx={{ bgcolor: 'rgba(76, 175, 80, 0.08)', borderRadius: 2, border: '1px solid rgba(76, 175, 80, 0.2)' }}>
                <Typography variant="subtitle2" color="text.secondary">INVOICE</Typography>
                <Typography variant="h6" fontWeight="bold">#{paymentInvoice.invoiceNumber}</Typography>
                <Box display="flex" justifyContent="space-between" mt={1}>
                  <Typography variant="body2" color="text.secondary">Amount Due:</Typography>
                  <Typography variant="body2" fontWeight="bold">₹{paymentInvoice.amount.toFixed(2)}</Typography>
                </Box>
              </Box>
            )}

            <FormControl fullWidth margin="dense">
              <InputLabel id="payment-mode-label">Payment Mode</InputLabel>
              <Select
                labelId="payment-mode-label"
                id="payment-mode"
                value={paymentMode}
                label="Payment Mode"
                onChange={(e) => setPaymentMode(e.target.value as any)}
              >
                <MenuItem value="cash">Cash</MenuItem>
                <MenuItem value="bank">Bank / UPI / Cheque</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              margin="dense"
              label="Payment Date"
              type="date"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              fullWidth
              margin="dense"
              label="Reference / Cheque Number"
              value={paymentReference}
              onChange={(e) => setPaymentReference(e.target.value)}
              placeholder="e.g. UPI Transaction ID, Cheque No."
            />

            {paymentMode === 'bank' && (
              <FormControl fullWidth margin="dense">
                <InputLabel id="bank-account-label">Deposit Account</InputLabel>
                <Select
                  labelId="bank-account-label"
                  id="bank-account"
                  value={selectedBankIdx}
                  label="Deposit Account"
                  onChange={(e) => setSelectedBankIdx(Number(e.target.value))}
                >
                  <MenuItem value={0}>Primary Bank Account</MenuItem>
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setRecordPaymentOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSavePayment}
            variant="contained"
            color="success"
            startIcon={<DoneAllIcon />}
            disabled={loading}
          >
            Record Payment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Delete Invoice</DialogTitle>
        <DialogContent dividers>
          <Typography>Are you sure you want to delete this invoice? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Invoices;
